import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { resolveAssetUrl } from '../utils/config';
import FileUploader, { ExistingFiles } from '../components/FileUploader';
import '../styles/formPage.css';

const BoardEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ title: '', content: '' });
  const [existingImages, setExistingImages] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newAttachments, setNewAttachments] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState(new Set());
  const [attachmentsToDelete, setAttachmentsToDelete] = useState(new Set());

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    API.get(`/board/${id}`)
      .then((res) => {
        if (ignore) return;
        const b = res.data.content;
        setForm({ title: b.title || '', content: b.content || '' });
        setExistingImages(b.images || []);
        setExistingAttachments(b.attachments || []);
      })
      .catch(() => { if (!ignore) setError('게시글을 불러오는데 실패했습니다.'); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [id]);

  const change = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const makeToggle = (setter) => (fileId) => setter((prev) => {
    const next = new Set(prev);
    if (next.has(fileId)) next.delete(fileId);
    else next.add(fileId);
    return next;
  });
  const toggleImage = makeToggle(setImagesToDelete);
  const toggleAttachment = makeToggle(setAttachmentsToDelete);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert('제목을 입력해주세요.');
    if (!form.content.trim()) return alert('내용을 입력해주세요.');

    try {
      setSubmitting(true);
      const keepImageIds = existingImages.filter((i) => !imagesToDelete.has(i.id)).map((i) => i.id);
      const keepAttachmentIds = existingAttachments.filter((a) => !attachmentsToDelete.has(a.id)).map((a) => a.id);

      const fd = new FormData();
      fd.append('update', new Blob([JSON.stringify({ title: form.title.trim(), content: form.content.trim() })], { type: 'application/json' }));
      newImages.forEach((img) => fd.append('images', img));
      newAttachments.forEach((att) => fd.append('attachments', att));
      // 항상 전송(빈 배열 포함) — 기존 파일을 전부 삭제하는 경우도 반영되도록.
      fd.append('keepImageIds', new Blob([JSON.stringify(keepImageIds)], { type: 'application/json' }));
      fd.append('keepAttachmentIds', new Blob([JSON.stringify(keepAttachmentIds)], { type: 'application/json' }));

      await API.put(`/board/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('게시글이 수정되었습니다.');
      navigate(`/board/${id}`);
    } catch (err) {
      alert(err.response?.data?.message || '게시글 수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('수정을 취소하시겠습니까? 변경사항이 저장되지 않습니다.')) navigate(`/board/${id}`);
  };

  if (loading) {
    return <div className="container section state-block"><div className="spinner" />불러오는 중...</div>;
  }
  if (error) {
    return (
      <div className="container section state-block">
        <p className="state-block__title">{error}</p>
        <button className="btn btn--outline btn--sm" onClick={() => navigate('/board')}>목록으로</button>
      </div>
    );
  }

  return (
    <div className="form-page container container--narrow section">
      <div className="form-page__head">
        <span className="section__eyebrow">Board</span>
        <h1 className="form-page__title">게시글 수정</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="field">
          <label className="field__label" htmlFor="title">제목 <span className="req">*</span></label>
          <input id="title" name="title" className="input" type="text" value={form.title} onChange={change} disabled={submitting} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="content">내용 <span className="req">*</span></label>
          <textarea id="content" name="content" className="textarea" value={form.content} onChange={change} disabled={submitting} />
        </div>

        <ExistingFiles label="기존 이미지" items={existingImages} deletedIds={imagesToDelete}
          onToggle={toggleImage} image resolveUrl={resolveAssetUrl} />
        <ExistingFiles label="기존 첨부파일" items={existingAttachments} deletedIds={attachmentsToDelete}
          onToggle={toggleAttachment} resolveUrl={resolveAssetUrl} />

        <FileUploader id="new-images" label="새 이미지 추가" accept="image/*" image
          files={newImages} onChange={setNewImages} disabled={submitting} />
        <FileUploader id="new-attachments" label="새 첨부파일 추가"
          files={newAttachments} onChange={setNewAttachments} disabled={submitting} />

        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={handleCancel} disabled={submitting}>취소</button>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? '수정 중...' : '수정하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BoardEdit;
